import React, { useEffect, useState } from "react";
import { PageSection } from "../common/pageSection/PageSection";
import {
  Card,
  makeStyles,
  tokens,
  Text,
  Divider,
  shorthands,
  mergeClasses,
} from "@fluentui/react-components";
import {
  CheckmarkCircleRegular,
  CheckmarkCircleFilled,
  SubtractCircleRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  PersonRegular,
  BuildingRegular,
  ClipboardTaskListLtrRegular,
} from "@fluentui/react-icons";
import { CtaButton } from "../common/input/CtaButton";
import { useNavigate } from "react-router";
import {
  GET_ADDITIONAL_MODULES,
  GET_BASE_MODULES,
  GET_CONFIGURATION,
  GET_PROCESSES,
  GET_SUBSCRIPTIONS,
} from "../../graphql/queries";
import { useQuery } from "@apollo/client";
import { AdditionalModule, BaseModule, Plan, Query } from "../../graphql/schema";
import { t } from "../../i18n/i18n";
import { clarityEvent, claritySetTag } from "../../lib/clarity";
import { formatPrice } from "../../helper/orderHelper";
import { useCmsLocale } from "../../i18n/cmsLocale";

const useStyles = makeStyles({
  pageSection: {
    backgroundColor: "#172433",
    color: "#FFFFFF",
  },
  titleWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    textAlign: "center",
    rowGap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalXXXL,
    marginBottom: tokens.spacingVerticalXXXL,
  },
  subHeader: {
    cursor: "pointer",
    color: "#B5B5B5",
    ...shorthands.textDecoration("underline"),
  },
  primaryRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    columnGap: tokens.spacingHorizontalXXXL,
    rowGap: tokens.spacingVerticalXXXL,
    marginBottom: tokens.spacingVerticalXXXL,

    "@media screen and (max-width: 1100px)": {
      gridTemplateColumns: "1fr",
    },
  },
  secondaryRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    columnGap: tokens.spacingHorizontalXXXL,
    rowGap: tokens.spacingVerticalXXXL,
    marginBottom: tokens.spacingVerticalXXXL,
    maxWidth: "66%",
    marginLeft: "auto",
    marginRight: "auto",

    "@media screen and (max-width: 1100px)": {
      gridTemplateColumns: "1fr",
      maxWidth: "100%",
    },
  },
  secondaryLabel: {
    display: "flex",
    justifyContent: "center",
    marginBottom: tokens.spacingVerticalL,
  },
  card: {
    ...shorthands.overflow("visible"),
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    borderTopRightRadius: "15px",
    borderTopLeftRadius: "15px",
    borderBottomRightRadius: "15px",
    borderBottomLeftRadius: "15px",

    marginTop: tokens.spacingVerticalXL,
    ...shorthands.padding(
      tokens.spacingHorizontalXL,
      tokens.spacingHorizontalXXXL,
    ),
    boxShadow: tokens.shadow8,
  },
  cardDefault: {
    color: "#FFFFFF",
    backgroundColor: "#2F4863",
  },
  highlightedCard: {
    backgroundColor: "#C9E3FF",
    transform: "scale(1.05)",
  },
  secondaryCard: {
    opacity: "0.85",
  },
  divider: {
    display: "block",
    flexGrow: 0,
    width: "60%",
    "::before": {
      ...shorthands.borderRadius("1000px"),
      backgroundColor: "#FFFFFF",
      height: "2px",
      ...shorthands.border(0),
    },
    "::after": { ...shorthands.border(0) },
  },
  dividerDefault: {
    "::before": {
      backgroundColor: "#FFFFFF",
    },
  },
  highlightedDivider: {
    "::before": {
      backgroundColor: tokens.colorBrandBackground,
    },
  },
  icon: {
    fontSize: "20px",
  },
  checkmarkColor: {
    color: "#88FF00",
  },
  checkmarkHighlightedColor: {
    color: "#30873F",
  },
  excludedIcon: {
    color: "#888888",
    fontSize: "18px",
  },
  featureWrapper: {
    display: "flex",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  excludedText: {
    color: "#888888",
  },
  semibold: {
    fontWeight: tokens.fontWeightSemibold,
  },
  bold: {
    fontWeight: tokens.fontWeightBold,
  },
  orderButton: {
    width: "100%",
    fontSize: tokens.fontSizeBase500,
    ...shorthands.padding(tokens.spacingVerticalM),
  },
  MostPopular: {
    position: "absolute",
    top: "-25px",
    right: "-25px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    textAlign: "center",

    height: "125px",
    width: "125px",

    backgroundColor: "#FFB327",
    color: "#ffffff",

    boxShadow: tokens.shadow16,

    ...shorthands.borderRadius("50%"),
  },
  noEmployeesBadge: {
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
  },
  noEmployeesBanner: {
    position: "absolute",
    top: "-25px",
    right: "-25px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    height: "125px",
    width: "125px",

    backgroundColor: "#4A6FA5",
    color: "#ffffff",
    textAlign: "center",

    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightBold,

    boxShadow: tokens.shadow16,
    ...shorthands.borderRadius("50%"),
  },
  tagline: {
    color: "#B5B5B5",
    textAlign: "center",
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
  },
  taglineHighlighted: {
    color: "#555555",
  },
  bottomSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalXXL,
    paddingTop: tokens.spacingVerticalL,
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: "rgba(255,255,255,0.15)",
    width: "100%",
  },
  bottomSectionHighlighted: {
    borderTopColor: "rgba(0,0,0,0.1)",
  },
  statRow: {
    display: "flex",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalS,
  },
  statIcon: {
    fontSize: "24px",
    opacity: "0.7",
  },
  showMoreWrapper: {
    display: "flex",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalXXL,
  },
  showMoreLine: {
    flexGrow: 1,
    height: "1px",
    backgroundColor: "rgba(255,255,255,0.15)",
  },
});

const SubscriptionCard = ({
  entity,
  showPrices,
  secondary = false,
  allBaseModules = [],
  allAdditionalModules = [],
  totalProcesses = 0,
}: {
  entity: Plan;
  showPrices: boolean;
  secondary?: boolean;
  allBaseModules?: BaseModule[];
  allAdditionalModules?: AdditionalModule[];
  totalProcesses?: number;
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const subscription = entity;
  const id = entity.documentId;

  return (
    <Card
      className={mergeClasses(
        subscription.highlighted
          ? classes.highlightedCard
          : classes.cardDefault,
        classes.card,
        secondary ? classes.secondaryCard : undefined,
      )}
    >
      {subscription.highlighted && (
        <div className={classes.MostPopular}>{t("MostPopular")}</div>
      )}

      <Text size={700} weight="bold">
        {subscription.title}
      </Text>

      {subscription.tagline && (
        <Text
          size={200}
          className={mergeClasses(
            classes.tagline,
            subscription.highlighted ? classes.taglineHighlighted : undefined,
          )}
        >
          {subscription.tagline}
        </Text>
      )}

      {subscription.noEmployees && (
        <div className={classes.noEmployeesBanner}>
          Utan
          <br />
          anställda
        </div>
      )}

      <Divider
        className={mergeClasses(
          classes.divider,
          subscription.highlighted
            ? classes.highlightedDivider
            : classes.dividerDefault,
        )}
      />

      {showPrices && (
        <>
          <Text size={600}>
            <span
              className={
                subscription.highlighted ? classes.bold : classes.semibold
              }
            >
              {formatPrice(subscription.price ?? 0)}
            </span>{" "}
            {t("CurrencyPerMonth")}
          </Text>
        </>
      )}

      <div>
        {allAdditionalModules.map((module) => {
          const included = (subscription.additionalModules ?? []).some(
            (m) => m?.documentId === module.documentId,
          );
          return (
            <div key={module.documentId} className={classes.featureWrapper}>
              {included ? (
                subscription.highlighted ? (
                  <CheckmarkCircleFilled
                    className={mergeClasses(
                      classes.icon,
                      classes.checkmarkHighlightedColor,
                    )}
                  />
                ) : (
                  <CheckmarkCircleRegular
                    className={mergeClasses(
                      classes.icon,
                      classes.checkmarkColor,
                    )}
                  />
                )
              ) : (
                <SubtractCircleRegular className={classes.excludedIcon} />
              )}
              <Text
                size={500}
                className={included ? undefined : classes.excludedText}
              >
                {module.name}
              </Text>
            </div>
          );
        })}
        {allBaseModules.map((module) => {
          const included = (subscription.baseModules ?? []).some(
            (m) => m?.documentId === module.documentId,
          );
          return (
            <div key={module.documentId} className={classes.featureWrapper}>
              {included ? (
                subscription.highlighted ? (
                  <CheckmarkCircleFilled
                    className={mergeClasses(
                      classes.icon,
                      classes.checkmarkHighlightedColor,
                    )}
                  />
                ) : (
                  <CheckmarkCircleRegular
                    className={mergeClasses(
                      classes.icon,
                      classes.checkmarkColor,
                    )}
                  />
                )
              ) : (
                <SubtractCircleRegular className={classes.excludedIcon} />
              )}
              <Text
                size={500}
                className={included ? undefined : classes.excludedText}
              >
                {module.name}
              </Text>
            </div>
          );
        })}
        {subscription.featuresList?.map((feature, idx) =>
          feature ? (
            <div key={idx} className={classes.featureWrapper}>
              {feature.included ? (
                subscription.highlighted ? (
                  <CheckmarkCircleFilled
                    className={mergeClasses(
                      classes.icon,
                      classes.checkmarkHighlightedColor,
                    )}
                  />
                ) : (
                  <CheckmarkCircleRegular
                    className={mergeClasses(
                      classes.icon,
                      classes.checkmarkColor,
                    )}
                  />
                )
              ) : (
                <SubtractCircleRegular className={classes.excludedIcon} />
              )}
              <Text
                size={500}
                className={feature.included ? undefined : classes.excludedText}
              >
                {feature.name}
              </Text>
            </div>
          ) : null,
        )}
      </div>

      <div
        className={mergeClasses(
          classes.bottomSection,
          subscription.highlighted
            ? classes.bottomSectionHighlighted
            : undefined,
        )}
      >
        {(subscription.includedProcesses ?? 0) > 0 && (
          <div className={classes.statRow}>
            <ClipboardTaskListLtrRegular className={classes.statIcon} />
            <Text size={500}>
              <span className={classes.semibold}>
                {subscription.includedProcesses}{" "}
              </span>
              {subscription.includedProcesses === 1 ? t("Process") : t("ProcessPlural")}
              {totalProcesses > 0 &&
              subscription.includedProcesses === totalProcesses
                ? ` ${t("AllProcesses")}`
                : ""}
            </Text>
          </div>
        )}
        <div className={classes.statRow}>
          <PersonRegular className={classes.statIcon} />
          <Text size={500}>
            <span className={classes.semibold}>{subscription.users} </span>
            {t("Users")}
          </Text>
        </div>
        <div className={classes.statRow}>
          <BuildingRegular className={classes.statIcon} />
          <Text size={500}>
            <span className={classes.semibold}>{subscription.workplaces}</span>
            {" "}{subscription.workplaces === 1 ? t("Workplace") : t("WorkplacePlural")}
          </Text>
        </div>
      </div>

      <CtaButton
        color={subscription.highlighted ? "alternative" : "secondary"}
        buttonText={subscription.buttonText ?? "Beställ nu"}
        className={classes.orderButton}
        onClick={() => {
          clarityEvent("order_started");
          claritySetTag("subscription", subscription.title ?? "");
          navigate(`/order/${id}`);
        }}
      />
    </Card>
  );
};

export const Subscriptions = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const locale = useCmsLocale();

  const { data, loading, error } = useQuery<Pick<Query, "subscriptions">>(
    GET_SUBSCRIPTIONS,
    { variables: { locale } },
  );
  const {
    data: configData,
    loading: configLoading,
    error: configError,
  } = useQuery(GET_CONFIGURATION);
  const { data: baseModulesData } = useQuery<Pick<Query, "baseModules">>(
    GET_BASE_MODULES,
    { variables: { locale } },
  );
  const allBaseModules: BaseModule[] = (
    baseModulesData?.baseModules ?? []
  ).filter(
    (m): m is BaseModule =>
      !!m &&
      m.name !== "Kravefterlevnad" &&
      m.name !== "Riskhantering",
  );
  const { data: additionalModulesData } = useQuery<
    Pick<Query, "additionalModules">
  >(GET_ADDITIONAL_MODULES, { variables: { locale } });
  const allAdditionalModules: AdditionalModule[] = (
    additionalModulesData?.additionalModules ?? []
  ).filter((m): m is AdditionalModule => !!m);
  const { data: processesData } = useQuery<Pick<Query, "processes">>(
    GET_PROCESSES,
    { variables: { locale } },
  );
  const totalProcesses: number = processesData?.processes?.length ?? 0;
  const [showPrices, setShowPrices] = useState(false);
  const [showSecondary, setShowSecondary] = useState(false);

  useEffect(() => {
    if (configLoading || configError) {
      setShowPrices(false);
      return;
    }
    const show = configData?.configuration?.showPrices;
    setShowPrices(show ?? false);
  }, [configLoading, configError, configData]);

  if (loading || error || !data?.subscriptions) return <></>;

  const allSubscriptions: Plan[] = (data?.subscriptions ?? []).filter(
    (s): s is Plan => !!s,
  );
  const primary = allSubscriptions.filter((e) => !e.secondary);
  const secondary = allSubscriptions.filter((e) => e.secondary);

  return (
    <div className={classes.pageSection}>
      <PageSection>
        <div className={classes.titleWrapper}>
          <Text
            size={800}
            weight="bold"
            style={{ textAlign: "center", display: "block" }}
          >
            {t("ChooseOneOfOurPackages")}
          </Text>
          <Text
            size={400}
            className={classes.subHeader}
            onClick={() => navigate("/faq")}
            style={{ textAlign: "center", display: "block" }}
          >
            {t("WhichSubscriptionLevelShouldIChoose")}
          </Text>
        </div>

        <div className={classes.primaryRow}>
          {primary.map((entity) => (
            <SubscriptionCard
              key={entity.documentId}
              entity={entity}
              showPrices={showPrices}
              allBaseModules={allBaseModules}
              allAdditionalModules={allAdditionalModules}
              totalProcesses={totalProcesses}
            />
          ))}
        </div>

        <div>
          {secondary.length > 0 && (
            <>
              <div className={classes.showMoreWrapper}>
                <div className={classes.showMoreLine} />
                <Text
                  size={400}
                  style={{
                    color: "#B5B5B5",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => setShowSecondary(!showSecondary)}
                >
                  {showSecondary ? t("HidePackages") : t("ShowMorePackages")}
                  {showSecondary ? (
                    <ChevronUpRegular />
                  ) : (
                    <ChevronDownRegular />
                  )}
                </Text>
                <div className={classes.showMoreLine} />
              </div>
              {showSecondary && (
                <div className={classes.secondaryRow}>
                  {secondary.map((entity) => (
                    <SubscriptionCard
                      key={entity.documentId}
                      entity={entity}
                      showPrices={showPrices}
                      secondary
                      allBaseModules={allBaseModules}
                      allAdditionalModules={allAdditionalModules}
                      totalProcesses={totalProcesses}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            textAlign: "center",
            marginTop: tokens.spacingVerticalXL,
            marginBottom: tokens.spacingVerticalXXL,
          }}
        >
          <Text size={400} style={{ color: "#B5B5B5" }}>
            {t("NoPackageSuitsYou")}{" "}
            <a
              href="/#contact"
              style={{ color: "#B5B5B5", textDecoration: "underline" }}
            >
              {t("ContactUsForQuote")}
            </a>
          </Text>
        </div>
      </PageSection>
    </div>
  );
};
